import { devErrLog } from "Util";
import { UserManager } from "./UserManager";
// @ts-ignore
import { EventSourcePolyfill } from "event-source-polyfill";

interface ObjectDataType {
  [key: string]: any;
}

export class FetchManager {
  userManager: UserManager;
  constructor() {
    this.userManager = new UserManager();
  }

  async setHeaders(data: object = {}) {
    const header = new Headers();
    if ((await this.userManager.getAccessToken()) !== null) {
      header.append("Authorization", `Bearer ${this.userManager.accessToken}`);
    }
    if (!(data instanceof FormData)) {
      header.append("Content-Type", "application/json; charset=UTF-8");
    }
    return header;
  }

  async initializeEventStream(url: string, data: ObjectDataType = {}) {
    try {
      const headers = await this.setHeaders(data);
      // headers.append("Cache-Control", "no-cache");

      const options = {
        // EventSourcePolyfill은 Headers 객체를 직접 사용할 수 없기 때문에, 일반 객체 형태로 변환해야 함
        // `Headers` 객체를 일반 객체로 변환
        headers: Object.fromEntries(headers.entries()),

        // 서버-클라이언트 간 연결 상태를 유지하기 위한 타임아웃 설정
        // 클라이언트는 45초 동안 서버가 아무 이벤트도 보내지 않으면 연결이 끊어진 것으로 간주 재연결을 시도.
        heartbeatTimeout: 45000,
      };
      const eventSource = new EventSourcePolyfill(url, options);
      // eventSource.onopen = () => {
      // };

      eventSource.onerror = () => {
        console.error("재연결 시도 중...");
      };

      return eventSource;
    } catch (e) {
      // devErrLog(e);
      return null;
    }
  }

  async get(
    url: string,
    data: ObjectDataType = {},
    signal: AbortSignal | null = null
  ) {
    try {
      return await this.executeFetch({ type: "GET", url, data, signal });
    } catch (e: any) {
      devErrLog(e);
    }
  }

  async post(
    url: string,
    data: ObjectDataType = {},
    signal: AbortSignal | null = null
  ) {
    try {
      return await this.executeFetch({ type: "POST", url, data, signal });
    } catch (e) {
      devErrLog(e);
    }
  }

  async put(
    url: string,
    data: ObjectDataType = {},
    signal: AbortSignal | null = null
  ) {
    try {
      return await this.executeFetch({ type: "PUT", url, data, signal });
    } catch (e) {
      devErrLog(e);
    }
  }

  async patch(
    url: string,
    data: ObjectDataType = {},
    signal: AbortSignal | null = null
  ) {
    try {
      return await this.executeFetch({ type: "PATCH", url, data, signal });
    } catch (e) {
      devErrLog(e);
    }
  }

  async delete(
    url: string,
    data: ObjectDataType = {},
    signal: AbortSignal | null = null
  ) {
    try {
      return await this.executeFetch({ type: "DELETE", url, data, signal });
    } catch (e) {
      devErrLog(e);
    }
  }

  download(url: string, data: ObjectDataType = {}) {
    try {
      let requestURL = "";
      if (data) {
        for (let key of Object.keys(data)) {
          const value = data[key];
          if (requestURL.length < 1) {
            if (value) requestURL += `?${key}=${value}`;
            else requestURL += `?${key}`;
          } else {
            if (value) requestURL += `&${key}=${value}`;
            else requestURL += `&${key}`;
          }
        }
        url += requestURL;
      }

      window.open(url, "_blank");
      return true;
    } catch (e) {
      devErrLog(e);
    }
  }

  async executeFetch(config: {
    type: string;
    url: string;
    data: ObjectDataType;
    signal: null | AbortSignal;
  }) {
    try {
      let { type, url, data, signal } = config;
      let requestURL = "";
      if (data && (type === "GET" || type === "DELETE")) {
        for (let key of Object.keys(data)) {
          const value = data[key];
          if (requestURL.length < 1) {
            if (value) requestURL += `?${key}=${value}`;
            else requestURL += `?${key}`;
          } else {
            if (value) requestURL += `&${key}=${value}`;
            else requestURL += `&${key}`;
          }
        }
        url += requestURL;
      }
      let header: Headers = await this.setHeaders(data);

      let fetchConfig: RequestInit = {
        method: type,
        mode: "cors",
        headers: header,
        redirect: "follow",
        signal,
      };
      if (this.isEmpty(header)) delete fetchConfig.headers;
      if (
        (type !== "GET" && type !== "DELETE" && Object.keys(data).length) ||
        data instanceof FormData
      ) {
        fetchConfig.body =
          data instanceof FormData ? data : JSON.stringify(data);
      }
      const response = await fetch(url, fetchConfig);
      return response;
    } catch (e: any) {
      let { url, type } = config;
      devErrLog(`${url} / ${type} 요청 실패`);
      devErrLog(e);

      // 해당 함수에서 에러를 내려주지 않으면 상위 함수에서 에러의 유형을 구분하기가 어려움
      return e;
    }
  }

  isEmpty(obj: object) {
    for (let key in obj) return false;
    return true;
  }
}
